import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Check, AlertCircle, Loader, Upload, Download, FileSpreadsheet
} from 'lucide-react';
import '../Boutiques.css';

export default function EcommerceImportStock() {
  const [boutiques,   setBoutiques]   = useState([]);
  const [boutiqueId,  setBoutiqueId]  = useState('');
  const [rows,        setRows]        = useState([]);
  const [mode,        setMode]        = useState('remplacement');
  const [error,       setError]       = useState('');
  const [importing,   setImporting]   = useState(false);
  const [result,      setResult]      = useState(null);
  const [toast,       setToast]       = useState({ type: '', text: '' });
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get('/api/boutiques')
      .then(({ data }) => setBoutiques(data))
      .catch(() => {});
  }, []);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast({ type: '', text: '' }), 4000);
  };

  const downloadStockTemplate = () => {
    const headers = ['Code Produit', 'Code Barres', 'Nom (info)', 'Quantité Stock'];
    const examples = [
      ['PROD-001', '', 'Produit exemple A', 100],
      ['', '3760123456789', 'Produit exemple B', 50],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...examples]);
    ws['!cols'] = [{ wch: 18 }, { wch: 18 }, { wch: 28 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock');
    XLSX.writeFile(wb, 'modele_import_stock_boutique.xlsx');
  };

  const handleFile = (ev) => {
    const file = ev.target.files?.[0];
    setError(''); setResult(null);
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (raw.length < 2) { setError('Le fichier ne contient pas de données'); return; }
        const parsed = [];
        for (let i = 1; i < raw.length; i++) {
          const [code, barre, nom, qte] = raw[i].map(v => String(v ?? '').trim());
          if (!code && !barre && !qte) continue;
          parsed.push({
            codeProduit: code,
            codeBarres: barre,
            nom,
            quantiteStock: Number(String(qte).replace(',', '.')) || 0,
          });
        }
        if (!parsed.length) { setError('Aucune ligne valide trouvée'); return; }
        setRows(parsed);
      } catch (err) {
        setError('Fichier invalide : ' + err.message);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleSubmit = async () => {
    if (!boutiqueId) { showToast('error', 'Sélectionnez une boutique'); return; }
    if (!rows.length) return;
    setImporting(true);
    setError('');
    try {
      const { data } = await axios.post(`/api/boutiques/${boutiqueId}/import-stock`, {
        mode,
        items: rows.map(r => ({
          codeProduit: r.codeProduit,
          codeBarres: r.codeBarres,
          quantiteStock: r.quantiteStock,
        })),
      });
      setResult(data.data);
      showToast('success', data.message || 'Import terminé');
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setRows([]); setResult(null); setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const selectedBoutique = boutiques.find(b => b._id === boutiqueId);

  return (
    <div className="bt-page">
      {toast.text && (
        <div className={`bt-toast bt-toast--${toast.type}`}>
          {toast.type === 'success' ? <Check size={14}/> : <AlertCircle size={14}/>}
          {toast.text}
        </div>
      )}

      <div className="bt-header">
        <div className="bt-header-left">
          <div className="bt-header-icon"><FileSpreadsheet size={20}/></div>
          <div>
            <h1 className="bt-header-title">Import du stock</h1>
            <p className="bt-header-sub">Mettez à jour le stock d'une boutique à partir d'un fichier Excel</p>
          </div>
        </div>
      </div>

      <div className="bt-content">
        <div className="bt-card" style={{ padding: 20 }}>

          {/* Choix boutique */}
          <div className="bt-field" style={{ marginBottom: 16, maxWidth: 360 }}>
            <label>Boutique *</label>
            <select value={boutiqueId}
              onChange={e => { setBoutiqueId(e.target.value); reset(); }}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, width: '100%' }}>
              <option value="">— Sélectionner une boutique —</option>
              {boutiques.map(b => <option key={b._id} value={b._id}>{b.nom}{!b.actif ? ' (inactive)' : ''}</option>)}
            </select>
          </div>

          {boutiqueId && !result && (
            <>
              <div className="bt-import-help">
                <span>Colonnes attendues : <code>Code Produit</code> (ou <code>Code Barres</code>), <code>Quantité Stock</code>.</span>
                <button type="button" className="bt-template-btn" onClick={downloadStockTemplate}>
                  <Download size={12}/> Télécharger le modèle
                </button>
              </div>

              <div className="bt-dropzone" onClick={() => fileRef.current?.click()}>
                <Upload size={20}/>
                <span>Cliquer pour sélectionner un fichier Excel</span>
                <small>.xlsx, .xls, .csv</small>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFile}/>
              </div>

              {error && <div className="bt-import-error"><AlertCircle size={12}/> {error}</div>}

              {rows.length > 0 && (
                <>
                  <div className="bt-import-mode">
                    <label className={`bt-mode-opt${mode === 'remplacement' ? ' bt-mode-opt--active' : ''}`}>
                      <input type="radio" name="impMode" value="remplacement"
                        checked={mode === 'remplacement'} onChange={() => setMode('remplacement')}/>
                      <div><strong>Remplacer</strong><span>La quantité écrase le stock actuel</span></div>
                    </label>
                    <label className={`bt-mode-opt${mode === 'ajout' ? ' bt-mode-opt--active' : ''}`}>
                      <input type="radio" name="impMode" value="ajout"
                        checked={mode === 'ajout'} onChange={() => setMode('ajout')}/>
                      <div><strong>Ajouter</strong><span>La quantité s'ajoute au stock actuel</span></div>
                    </label>
                  </div>

                  <div className="bt-import-preview">
                    <div className="bt-preview-head">Aperçu — {rows.length} ligne{rows.length > 1 ? 's' : ''}</div>
                    <div className="bt-preview-table-wrap">
                      <table className="bt-preview-table">
                        <thead>
                          <tr><th>#</th><th>Code produit</th><th>Code-barres</th><th>Nom</th><th>Quantité</th></tr>
                        </thead>
                        <tbody>
                          {rows.slice(0, 50).map((r, i) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>{r.codeProduit || '—'}</td>
                              <td>{r.codeBarres || '—'}</td>
                              <td>{r.nom || '—'}</td>
                              <td><strong>{r.quantiteStock}</strong></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {rows.length > 50 && (
                        <div className="bt-preview-more">+ {rows.length - 50} ligne(s) supplémentaire(s)…</div>
                      )}
                    </div>
                  </div>

                  <div className="bt-modal-footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
                    <button type="button" className="bt-btn-cancel" onClick={reset} disabled={importing}>Réinitialiser</button>
                    <button type="button" className="bt-btn-confirm" disabled={importing || !rows.length} onClick={handleSubmit}>
                      {importing
                        ? <><Loader size={13} className="bt-spin"/> Import en cours…</>
                        : <><Upload size={13}/> Importer dans {selectedBoutique?.nom} ({rows.length})</>}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {result && (
            <div className="bt-import-result">
              <div className="bt-import-stats">
                <div className="bt-stat bt-stat--blue"><strong>{result.misAJour}</strong><span>Mis à jour</span></div>
                <div className="bt-stat bt-stat--grey"><strong>{result.inchange}</strong><span>Inchangé(s)</span></div>
                <div className="bt-stat bt-stat--red"><strong>{result.erreurs?.length || 0}</strong><span>Erreur(s)</span></div>
              </div>
              {result.erreurs?.length > 0 && (
                <div className="bt-import-errors">
                  <div className="bt-preview-head">Erreurs</div>
                  <ul>
                    {result.erreurs.slice(0, 20).map((er, i) => (
                      <li key={i}>Ligne {er.ligne} {er.codeProduit ? `(${er.codeProduit})` : ''} : {er.erreur}</li>
                    ))}
                    {result.erreurs.length > 20 && <li>+ {result.erreurs.length - 20} autre(s)…</li>}
                  </ul>
                </div>
              )}
              <div className="bt-modal-footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
                <button type="button" className="bt-btn-confirm" onClick={reset}>
                  <Check size={13}/> Nouvel import
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
